/**
 * React Query configuration
 *
 * @description Configuración centralizada de React Query con opciones
 * optimizadas para performance y UX
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Configuración del QueryClient con defaults optimizados
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Tiempo antes de que los datos se consideren obsoletos (5 minutos)
      staleTime: 5 * 60 * 1000,

      // Tiempo antes de que los datos en caché se eliminen (10 minutos)
      gcTime: 10 * 60 * 1000,

      // Retry reducido a 1 para evitar rate limiting (429 errors)
      retry: 1,
      retryDelay: 2000, // 2 segundos entre retries

      // Refetch automático cuando la ventana recupera el foco
      refetchOnWindowFocus: false,

      // Refetch desactivado para evitar peticiones innecesarias
      refetchOnReconnect: false,

      // Refetch desactivado - usar cache si está disponible
      refetchOnMount: false,
    },
    mutations: {
      // Sin retries en mutaciones para evitar duplicados
      retry: 0,
      retryDelay: 1000,
    },
  },
});

/**
 * Query keys centralizadas para consistencia
 *
 * @description Centralizar las query keys facilita la invalidación
 * y evita typos
 */
export const queryKeys = {
  // Productos
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters: string) => [...queryKeys.products.lists(), { filters }] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.products.details(), id] as const,
    byPos: (posId: number) => [...queryKeys.products.all, 'byPos', posId] as const,
  },

  // Categorías
  categories: {
    all: ['categories'] as const,
    lists: () => [...queryKeys.categories.all, 'list'] as const,
    list: (filters?: string) => [...queryKeys.categories.lists(), { filters }] as const,
  },

  // Menús
  menus: {
    all: ['menus'] as const,
    weekly: () => [...queryKeys.menus.all, 'weekly'] as const,
    byDate: (date: string) => [...queryKeys.menus.all, 'byDate', date] as const,
  },

  // Usuarios
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (page: number, limit: number) =>
      [...queryKeys.users.lists(), { page, limit }] as const,
    search: (query: string) => [...queryKeys.users.all, 'search', query] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    stats: () => [...queryKeys.users.all, 'stats'] as const,
    roles: () => [...queryKeys.users.all, 'roles'] as const,
  },

  // Órdenes
  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.orders.lists(), filters] as const,
    details: () => [...queryKeys.orders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
    userOrders: (userId?: string) =>
      [...queryKeys.orders.all, 'user', userId || 'me'] as const,
  },

  // POS
  pos: {
    all: ['pos'] as const,
    lists: () => [...queryKeys.pos.all, 'list'] as const,
    details: () => [...queryKeys.pos.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.pos.details(), id] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    admin: () => [...queryKeys.dashboard.all, 'admin'] as const,
    student: () => [...queryKeys.dashboard.all, 'student'] as const,
    apoderado: () => [...queryKeys.dashboard.all, 'apoderado'] as const,
  },

  // Transacciones
  transactions: {
    all: ['transactions'] as const,
    lists: () => [...queryKeys.transactions.all, 'list'] as const,
    list: (filters: Record<string, any>) =>
      [...queryKeys.transactions.lists(), filters] as const,
  },
};

/**
 * Helpers para invalidación de queries
 */
export const invalidateQueries = {
  products: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all }),
  categories: () => queryClient.invalidateQueries({ queryKey: queryKeys.categories.all }),
  menus: () => queryClient.invalidateQueries({ queryKey: queryKeys.menus.all }),
  users: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all }),
  orders: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
  pos: () => queryClient.invalidateQueries({ queryKey: queryKeys.pos.all }),
  dashboard: () => queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all }),
  transactions: () => queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all }),
};
